import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { searchCoverage } from '../../services/api/fhirAPI/coverage/coverage.js'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'
export interface RecentCoverage {
	[key: string]: FhirCoverage
}

const coverageSpan: number = 36

function xMonthsFromToday(months: number = coverageSpan) {
	const today = new Date()
	const dateMax = new Date(today)
	const newDate = new Date(dateMax.setMonth(dateMax.getMonth() - months))

	return newDate
}

function withinXMonths(val: string, months: number = coverageSpan) {
	const today = new Date()
	const dateMax = new Date(today)
	dateMax.setMonth(dateMax.getMonth() - months)
	const dateToCheck = new Date(val)
	return dateMax <= dateToCheck
}

export const useCoverageStore = defineStore(
	'coverageStore',
	() => {
		// ====---------------====
		// State
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()
		const coverage = ref<CoverageState>({})
		const loaded = ref(false)

		// ====---------------====
		// Getters
		// ====---------------====

		// Gets the most recent coverage for each member
		// This is used for most calls (group IDs/coverage and copays)

		function getRecentCoverage(): RecentCoverage {
			const mostRecent: RecentCoverage = {}

			for (const memberId in coverage.value) {
				if (coverage.value?.[memberId]?.[0]) mostRecent[memberId] = coverage.value[memberId][0]
			}

			return mostRecent
		}

		function getCoverageByIndex(index: number): RecentCoverage {
			const mostRecent: RecentCoverage = {}

			for (const memberId in coverage.value) {
				if (coverage.value?.[memberId]?.[index])
					mostRecent[memberId] = coverage.value[memberId][index]
			}

			return mostRecent
		}

		// Gets the current and previous plans years
		// Used for Spending details
		function getCurrentPreviousCoverage(dateRange: string): FhirCoverage[] {
			const contracts: FhirCoverage[] = []

			for (const member in coverage.value) {
				const planList = coverage.value[member]
				if (!planList) continue

				planList.forEach((plan: FhirCoverage) => {
					const rangeDate = new Date(dateRange)
					const planDate = plan.period?.end ? new Date(plan.period?.end) : null

					if (planDate && planDate >= rangeDate) contracts.push(plan)
				})
			}

			return contracts
		}

		function getCoverageByYear(year: number): FhirCoverage[] {
			const contracts: FhirCoverage[] = []

			for (const member in coverage.value) {
				const planList = coverage.value[member]
				if (!planList) continue

				contracts.push(
					...planList.filter((plan) => {
						if (plan.period?.start) {
							const planYear = new Date(`${plan.period.start.replaceAll('-', '/')}`).getFullYear()
							return planYear === year
						}
						return false
					})
				)
			}

			return contracts
		}

		function getCoverageByPeriodStart(start: string): FhirCoverage[] {
			const contracts: FhirCoverage[] = []

			for (const member in coverage.value) {
				const planList = coverage.value[member]
				if (!planList) continue

				contracts.push(
					...planList.filter((plan) => {
						if (plan.period?.start) {
							const planStart = plan.period.start
							return planStart === start
						}
						return false
					})
				)
			}

			return contracts
		}

		function getRxTiers() {
			const copayTiers =
				coverage.value[planStore.memberId]?.[0]?.costToBeneficiary
					?.filter((x: FhirCost) => {
						return (
							x.type.coding[0]?.code?.includes('rx_tier') &&
							(x.type.coding[0].code.includes('copay') || x.type.coding[0].code.includes('coins'))
						)
					})
					.map((x: FhirCost) => {
						if (x.type.coding[0].code?.includes('copay')) {
							return {
								label: x.type.text.replace('RX ', '').replace('Copay', ''),
								amount: `$${x?.valueMoney.value?.toFixed(2)}`
							}
						} else if (x.type.coding[0].code?.includes('coins')) {
							return {
								label: x.type.text.replace('RX ', '').replace('Coinsurance', ''),
								amount: `${x?.valueQuantity?.value}% coinsurance`
							}
						}
					}) ?? []
			return copayTiers.length > 0 ? copayTiers : false
		}

		// ====---------------====
		// Actions
		// ====---------------====
		function updateCoverage(memberId: string, payload: object) {
			if (coverage.value[memberId]) {
				coverage.value[memberId].push(payload)
			} else {
				coverage.value[memberId] = []
				coverage.value[memberId].push(payload)
			}
			loaded.value = true
		}

		let loadCoverageRequest: Promise<string> | null = null
		const loadCoverageStore = async () => {
			if (loadCoverageRequest) {
				return loadCoverageRequest
			}
			loadCoverageRequest = new Promise(async (resolve, reject) => {
				try {
					let apiPlanId = planStore.isPolicyHolder ? planStore.contractId : planStore.memberId
					await searchCoverage(
						apiPlanId,
						`&versionId=2&_count=50&period=ge${xMonthsFromToday().getFullYear()}-01-01`
					)
						.then((res) => {
							if (res.total === 0) return
							res.loaded = true
							res.entry.forEach((covItem) => {
								const covItemType = covItem.resource.type.coding[0].code.toLowerCase()
								// check if active AND not already in store
								if (
									covItem.resource.status === 'active' ||
									(covItem.resource.status === 'cancelled' &&
										withinXMonths(covItem.resource.period.end, coverageSpan))
								) {
									// HIP
									if (covItemType === 'hip' && covItem.resource.id.includes(planStore.contractId)) {
										updateCoverage(covItem.resource.id, covItem.resource)
									}
									// Dental
									else if (
										covItemType === 'dental' &&
										covItemType === planStore?.contractType?.toLowerCase() &&
										covItem.resource.id.includes(planStore?.contractId)
									) {
										updateCoverage(covItem.resource.id, covItem.resource)
									}
									// Vision
									else if (
										covItemType === 'vision' &&
										covItemType === planStore?.contractType?.toLowerCase() &&
										covItem.resource.id.includes(planStore.contractId)
									) {
										updateCoverage(covItem.resource.id, covItem.resource)
									}
								}
							})
						})
						.catch((err: string) => {
							console.error(err)
						})

					resolve('')
				} catch (err) {
					reject(err)
				} finally {
					loadCoverageRequest = null
				}
			})
			return loadCoverageRequest
		}

		function $reset() {
			coverage.value = {}
			loaded.value = false
		}

		return {
			coverage,
			loaded,
			getRecentCoverage,
			getCoverageByIndex,
			getCurrentPreviousCoverage,
			getCoverageByYear,
			getCoverageByPeriodStart,
			getRxTiers,
			loadCoverageStore,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
